import { FC, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button, MyModal, TextAreaField } from 'src/components/ui'
import { useReportReviewMutation } from 'src/redux/api'

const REASONS = [
  'SPAM',
  'HARASSMENT',
  'INAPPROPRIATE_CONTENT',
  'SCAM',
  'OFFENSIVE_LANGUAGE',
  'OTHER',
] as const

interface IReviewReportModal {
  reviewId: string
  open: boolean
  onClose: () => void
}

// Reporting a review left about you (H1 rework). Reuses the shared report.*
// copy and reasons so it matches the message-report modal.
const ReviewReportModal: FC<IReviewReportModal> = ({ reviewId, open, onClose }) => {
  const { t } = useTranslation()
  const [reportReview, { isLoading }] = useReportReviewMutation()
  const [reason, setReason] = useState<string>('')
  const [description, setDescription] = useState('')

  const submit = async () => {
    if (!reason) return
    try {
      await reportReview({
        reviewId,
        reason,
        description: description.trim() || undefined,
      }).unwrap()
      toast.success(t('report.submitted', 'Report sent'))
      setReason('')
      setDescription('')
      onClose()
    } catch {
      // toastMiddleware surfaces the backend reason
    }
  }

  return (
    <MyModal
      open={open}
      handleClose={onClose}
      width="w-[calc(100%_-_2rem)] md:w-auto md:min-w-[26rem]"
    >
      <div className="flex flex-col gap-4 p-5">
        <h3 className="text-lg font-black text-ink">{t('report.title')}</h3>
        <div className="flex flex-wrap gap-2">
          {REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              className={clsx(
                'px-3 py-1 text-sm rounded-full border transition-colors',
                reason === r
                  ? 'bg-brand-strong text-white border-brand'
                  : 'border-ink/30 text-ink hover:text-brand',
              )}
            >
              {t(`report.reason.${r}`)}
            </button>
          ))}
        </div>
        <TextAreaField
          name="reviewReportDescription"
          className="!h-20 !border !border-ink/[0.18]"
          placeholder={t('report.description_placeholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Button type="button" disabled={isLoading || !reason} onClick={submit}>
          {t('report.submit')}
        </Button>
      </div>
    </MyModal>
  )
}

export default ReviewReportModal
